import { HEADER_EXPORT_TYPE, MIMETYPE_DISY_WORKORDER_ID } from '@disy/lib.backend/api-constants';
import { assert, assertNonNullable } from '@disy/lib/custom-error';
import {
  MIMETYPE_CSV,
  MIMETYPE_GEOPACKAGE,
  MIMETYPE_GPX,
  MIMETYPE_KML,
  MIMETYPE_SHAPEFILE,
  MIMETYPE_XLSX,
} from '@disy/lib/media-type';

import { ajax } from 'cadenza/api-client/api';
import type { UploadedFileDto } from 'cadenza/api-client/dto/uploaded-file-dto';
import { toSearchParams } from 'cadenza/api-client/dto/uploaded-file-dto';
import type { Authority } from 'cadenza/api-client/srs-api';
import { createWorkbookUrl } from 'cadenza/api-client/workbook-api';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import { workbookNameAndHashString } from 'cadenza/repository/model/workbook-name-and-hash';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ExportActionType, ExportEntityType } from 'cadenza/web-analytics/web-analytics-api';
import { dispatchWebAnalyticsEvent } from 'cadenza/web-analytics/web-analytics-api';
import type { ExportActionContext } from 'cadenza/workbook/actions/export-actions';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import {
  getActiveWorksheet,
  getDataViewById,
  getViewByDataViewId,
  getViewById,
  getViewTypeByDataViewId,
  getWorkbookPrintName,
} from 'cadenza/workbook/workbook-core-selectors.state';

export function exportMultipleDataViewData({
  authority,
  exportType,
  dataViewId,
  mediaType,
  uploadedFile,
  workbookNameAndHash,
}: {
  authority?: Authority;
  exportType: Extract<ExportEntityType, 'workbook' | 'worksheet' | 'view'>;
  dataViewId: DataViewId[];
  mediaType: string;
  uploadedFile?: UploadedFileDto;
  workbookNameAndHash: WorkbookNameAndHash;
}) {
  const entityPrintName = getPrintName(exportType, dataViewId[0]);
  const searchParams = new URLSearchParams(uploadedFile && toSearchParams(uploadedFile));
  if (authority) {
    searchParams.append('authority', authority);
  }
  if (exportType !== 'view') {
    searchParams.append('baseFileName', entityPrintName);
  }
  const url = `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/data-views;dataViewIds=${dataViewId.join()}/export-work-orders?${searchParams}`;

  dispatchWebAnalyticsEvent({
    category: 'export',
    action: mapMediaTypeToActionType(mediaType),
    entityType: exportType,
    entityPrintName,
    workbookPrintName: getWorkbookPrintName(getStore().getState()),
    worksheetPrintName: exportType !== 'workbook' ? getActiveWorksheet(getStore().getState()).printName : undefined,
  });
  return ajax<WorkOrderId>(url, {
    method: 'POST',
    headers: { [HEADER_EXPORT_TYPE]: mediaType, Accept: MIMETYPE_DISY_WORKORDER_ID },
  });
}

export function checkIfDataViewsDataCanBeExported(
  workbookNameAndHash: WorkbookNameAndHash,
  dataViewIds: DataViewId[],
  mediaType: string,
  actionContext?: ExportActionContext,
) {
  if (actionContext?.type === 'ATTRIBUTE_TABLE') {
    return checkIfAttributeTableDataCanBeExported(workbookNameAndHash, dataViewIds[0], mediaType, actionContext);
  }
  const searchParams = new URLSearchParams();
  if (actionContext?.type === 'SELECTION_CART') {
    searchParams.append('useSelection', 'true');
  }
  const url =
    createWorkbookUrl(workbookNameAndHash) +
    `/data-views;dataViewIds=${dataViewIds.join(',')}/can-export-work-orders?${searchParams}`;
  return ajax<WorkOrderId>(url, {
    method: 'OPTIONS',
    headers: { [HEADER_EXPORT_TYPE]: mediaType },
  });
}

function checkIfAttributeTableDataCanBeExported(
  workbookNameAndHash: WorkbookNameAndHash,
  dataViewId: DataViewId,
  mediaType: string,
  actionContext: ExportActionContext,
) {
  const searchParams = new URLSearchParams();
  assert(actionContext.type === 'ATTRIBUTE_TABLE', 'Intended only for handling context for ATTRIBUTE_TABLE');

  searchParams.set('bbox', actionContext.extent.join());
  searchParams.set('srsCode', actionContext.srs);
  const url = `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/data-views/${dataViewId}/attribute-table/can-export-work-orders?${searchParams}`;
  return ajax<WorkOrderId>(url, {
    method: 'POST',
    headers: { [HEADER_EXPORT_TYPE]: mediaType, Accept: MIMETYPE_DISY_WORKORDER_ID },
  });
}

interface ExportDataViewDataOptions {
  authority?: Authority;
  workbookNameAndHash: WorkbookNameAndHash;
  dataViewId: DataViewId;
  mediaType: string;
  actionContext?: ExportActionContext;
  uploadedFile?: UploadedFileDto;
}

export function exportDataViewData(options: ExportDataViewDataOptions) {
  if (options.actionContext?.type === 'ATTRIBUTE_TABLE') {
    return exportAttributeTable(options);
  }

  const { authority, dataViewId, mediaType, actionContext, uploadedFile, workbookNameAndHash } = options;
  const searchParams = uploadedFile ? toSearchParams(uploadedFile) : new URLSearchParams();
  if (authority) {
    searchParams.append('authority', authority);
  }
  if (actionContext?.type === 'SELECTION_CART') {
    searchParams.append('useSelection', String(true));
  }
  const url = createWorkbookUrl(workbookNameAndHash, { dataViewId }) + `?${searchParams}`;
  const entityType = getViewTypeByDataViewId(getStore().getState(), dataViewId) === 'MAP' ? 'layer' : 'view';
  dispatchWebAnalyticsEvent({
    category: 'export',
    action: mapMediaTypeToActionType(mediaType),
    entityType,
    entityPrintName: getPrintName(entityType, dataViewId),
    workbookPrintName: getWorkbookPrintName(getStore().getState()),
    worksheetPrintName: getActiveWorksheet(getStore().getState()).printName,
  });
  return ajax<WorkOrderId>(url, { headers: { [HEADER_EXPORT_TYPE]: mediaType, Accept: MIMETYPE_DISY_WORKORDER_ID } });
}

function exportAttributeTable({
  dataViewId,
  mediaType,
  actionContext,
  uploadedFile,
  workbookNameAndHash,
}: ExportDataViewDataOptions) {
  assertNonNullable(actionContext);
  assert(actionContext.type === 'ATTRIBUTE_TABLE', 'Intended only for handling context for ATTRIBUTE_TABLE');
  const searchParams = uploadedFile ? toSearchParams(uploadedFile) : new URLSearchParams();
  searchParams.set('bbox', actionContext.extent.join());
  searchParams.set('srsCode', actionContext.srs);
  const url = `/workbooks/${workbookNameAndHashString(workbookNameAndHash)}/data-views/${dataViewId}/attribute-table/export-work-orders?${searchParams}`;

  const entityType = 'layer';
  dispatchWebAnalyticsEvent({
    category: 'export',
    action: mapMediaTypeToActionType(mediaType),
    entityType,
    entityPrintName: getPrintName(entityType, dataViewId),
    workbookPrintName: getWorkbookPrintName(getStore().getState()),
    worksheetPrintName: getActiveWorksheet(getStore().getState()).printName,
  });
  return ajax<WorkOrderId>(url, {
    method: 'POST',
    headers: { [HEADER_EXPORT_TYPE]: mediaType, Accept: MIMETYPE_DISY_WORKORDER_ID },
  });
}

function getPrintName(exportType: ExportEntityType, dataViewId: DataViewId) {
  if (exportType === 'workbook') {
    return getWorkbookPrintName(getStore().getState());
  }
  if (exportType === 'worksheet') {
    return getActiveWorksheet(getStore().getState()).printName;
  }
  if (exportType === 'layer') {
    return getDataViewById(getStore().getState(), dataViewId).name;
  }
  return getViewByDataViewId(getStore().getState(), dataViewId).name;
}

function mapMediaTypeToActionType(mediaType: string): Exclude<ExportActionType, 'curated_report'> {
  switch (mediaType) {
    case MIMETYPE_CSV:
      return 'csv';
    case MIMETYPE_XLSX:
      return 'excel';
    case MIMETYPE_GEOPACKAGE:
      return 'geopackage';
    case MIMETYPE_KML:
      return 'kml';
    case MIMETYPE_GPX:
      return 'gpx';
    case MIMETYPE_SHAPEFILE:
      return 'shapefile';
    default:
      throw new Error(`Unsupported media type: ${mediaType}`);
  }
}

/**
 * Checks if currently visible drillThrough result can be downloaded with specified mimeType.
 * Check takes into account export limits configured in the system.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param mediaType - MIME type of expected result
 * @return Export check result
 */
export function checkIfIndividualValuesTableCanBeExported(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  mediaType: string,
) {
  return ajax<WorkOrderId>(createWorkbookUrl(workbookNameAndHash, { viewId }) + '/individualValues', {
    method: 'OPTIONS',
    headers: { [HEADER_EXPORT_TYPE]: mediaType },
  });
}

export interface IndividualValuesTableDataExportOptions {
  orderByColumnName?: string;
  orderAsc?: boolean;
  targetFile?: UploadedFileDto;
}

/**
 * Starts download of currently visible drillThrough result. Result will be prepared accordingly to provided mimeType.
 *
 * @param workbookNameAndHash - Name and hash of the workbook
 * @param viewId - Id of the view
 * @param mediaType - MIME type of expected result
 * @param parameters - Request parameters
 * @param [parameters.orderByColumnName] - Attribute of a column to sort
 * @param [parameters.orderAsc] - Whether to sort in ascending order
 * @param [parameters.targetFile] - Details of target file to append data to
 * @return Export result
 */
export function exportIndividualValuesTableData(
  workbookNameAndHash: WorkbookNameAndHash,
  viewId: ViewId,
  mediaType: string,
  { orderByColumnName, orderAsc, targetFile }: IndividualValuesTableDataExportOptions = {},
) {
  const baseUrl = createWorkbookUrl(workbookNameAndHash, { viewId });
  let data = orderByColumnName ? { orderByColumnName, orderAsc } : {};
  if (targetFile) {
    data = Object.assign(data, targetFile);
  }
  const headers = { Accept: MIMETYPE_DISY_WORKORDER_ID, [HEADER_EXPORT_TYPE]: mediaType };
  dispatchWebAnalyticsEvent({
    category: 'export',
    action: mapMediaTypeToActionType(mediaType),
    entityType: 'view_individual_values',
    entityPrintName: getViewById(getStore().getState(), viewId).name,
    workbookPrintName: getWorkbookPrintName(getStore().getState()),
    worksheetPrintName: getActiveWorksheet(getStore().getState()).printName,
  });
  return ajax<WorkOrderId>(`${baseUrl}/individualValues`, { data, headers });
}
